'use client'

import { useEffect, useRef, useState } from 'react'
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Clock3,
  Download,
  FileArchive,
  FileUp,
  Hash,
  Loader2,
  LockKeyhole,
  RefreshCw,
  ShieldCheck,
  UploadCloud,
  X,
  XCircle,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ContentPage } from '@/components/content-page'

type User = {
  id: number
  handle: string | null
  disclaimerAccepted: boolean
}

type FileDropModeration = {
  status: string
  decision: string | null
  riskLevel: 'low' | 'caution' | 'danger'
  reasons: string[]
  categories: string[]
  confidence: number | null
  error: string | null
}

type FileDrop = {
  id: number
  originalName: string
  description: string
  mimeType: string
  detectedType: string
  sizeBytes: number
  sha256: string
  status: 'pending' | 'approved' | 'rejected'
  uploaderHandle: string
  createdAt: string
  reviewedAt: string | null
  reviewNote: string
  canDownload: boolean
  downloadUrl: string | null
  isOwner: boolean
  moderation: FileDropModeration | null
}

function formatFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

function formatDate(value: string) {
  const date = new Date(value.includes('T') ? value : `${value.replace(' ', 'T')}Z`)
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function riskStyle(risk: FileDropModeration['riskLevel']) {
  if (risk === 'danger') {
    return 'border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300'
  }
  if (risk === 'caution') {
    return 'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300'
  }
  return 'border-border bg-secondary text-secondary-foreground'
}

function statusLabel(file: FileDrop) {
  if (file.status === 'approved') return 'Admin approved'
  if (file.status === 'rejected') return 'Rejected'
  return 'In quarantine'
}

function StatusIcon({ status }: { status: FileDrop['status'] }) {
  if (status === 'approved') return <CheckCircle2 className="h-4 w-4" />
  if (status === 'rejected') return <XCircle className="h-4 w-4" />
  return <Clock3 className="h-4 w-4" />
}

function FileCard({ file }: { file: FileDrop }) {
  const risk = file.moderation?.riskLevel || 'caution'
  const reasons = file.moderation?.reasons || []
  return (
    <article className="rounded-xl border border-border bg-card p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-border bg-background text-primary">
            <FileArchive className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <h3 className="break-all font-semibold">{file.originalName}</h3>
            <p className="mt-1 text-xs text-muted-foreground">
              {formatFileSize(file.sizeBytes)} · {file.detectedType.replaceAll('-', ' ')} · uploaded
              by {file.uploaderHandle} · {formatDate(file.createdAt)}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-2.5 py-1 text-[11px] font-medium">
            <StatusIcon status={file.status} />
            {statusLabel(file)}
          </span>
          <span
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium ${riskStyle(risk)}`}
          >
            <AlertTriangle className="h-3.5 w-3.5" />
            {risk === 'danger' ? 'High risk' : risk === 'caution' ? 'Needs review' : 'No flags found'}
          </span>
        </div>
      </div>

      {file.description && (
        <p className="mt-3 whitespace-pre-wrap text-sm text-muted-foreground">{file.description}</p>
      )}

      {reasons.length > 0 && file.status === 'pending' && (
        <div className="mt-3 rounded-lg border border-border bg-background px-3 py-2">
          <p className="text-xs font-medium">Review signals</p>
          <ul className="mt-1 list-disc space-y-1 pl-4 text-xs text-muted-foreground">
            {reasons.slice(0, 5).map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>
          {file.moderation?.status === 'queued' || file.moderation?.status === 'processing' ? (
            <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Additional metadata checks are running.
            </p>
          ) : file.moderation?.error ? (
            <p className="mt-2 text-xs text-destructive">
              Automated metadata check failed; administrator review is still required.
            </p>
          ) : null}
        </div>
      )}

      {file.reviewNote && (
        <p className="mt-3 text-xs text-muted-foreground">
          Administrator note: {file.reviewNote}
        </p>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
        <span className="flex min-w-0 items-center gap-1.5 font-mono">
          <Hash className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate" title={file.sha256}>
            {file.sha256}
          </span>
        </span>
        {file.canDownload && file.downloadUrl && (
          <Button
            size="sm"
            variant="outline"
            nativeButton={false}
            render={<a href={file.downloadUrl} />}
          >
            <Download className="h-3.5 w-3.5" />
            Download
          </Button>
        )}
      </div>
    </article>
  )
}

export default function FilesPage() {
  const [user, setUser] = useState<User | null>(null)
  const [files, setFiles] = useState<FileDrop[]>([])
  const [maxFileBytes, setMaxFileBytes] = useState(100 * 1024 * 1024)
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [description, setDescription] = useState('')
  const [dragging, setDragging] = useState(false)
  const [notice, setNotice] = useState('')
  const [error, setError] = useState('')
  const fileInput = useRef<HTMLInputElement>(null)

  async function loadFiles() {
    const response = await fetch('/api/file-drops')
    if (response.status === 401) {
      setUser(null)
      return
    }
    const data = (await response.json().catch(() => ({}))) as {
      error?: string
      files?: FileDrop[]
      maxFileBytes?: number
    }
    if (!response.ok) throw new Error(data.error || 'Could not load the file drop.')
    setFiles(data.files || [])
    if (data.maxFileBytes) setMaxFileBytes(data.maxFileBytes)
  }

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const meResponse = await fetch('/api/me')
        const meData = (await meResponse.json()) as { user: User | null }
        if (cancelled) return
        setUser(meData.user)
        if (meData.user?.disclaimerAccepted) await loadFiles()
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Could not load the file drop.')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!user?.disclaimerAccepted) return
    if (!files.some((file) => file.status === 'pending')) return
    const timer = window.setInterval(() => {
      void loadFiles().catch(() => {
        // Keep the last successful list during a transient refresh failure.
      })
    }, 20_000)
    return () => window.clearInterval(timer)
  }, [user?.disclaimerAccepted, files.some((file) => file.status === 'pending')])

  function chooseFile(file: File | null) {
    setError('')
    setNotice('')
    if (!file) return
    if (file.size > maxFileBytes) {
      setSelectedFile(null)
      setError(`That file is larger than the ${Math.floor(maxFileBytes / 1024 / 1024)} MB limit.`)
      return
    }
    if (file.size === 0) {
      setSelectedFile(null)
      setError('Choose a non-empty file.')
      return
    }
    setSelectedFile(file)
  }

  async function uploadFile(event: React.FormEvent) {
    event.preventDefault()
    if (!selectedFile) {
      setError('Choose a file first.')
      return
    }
    setUploading(true)
    setError('')
    setNotice('')
    try {
      const response = await fetch('/api/file-drops', {
        method: 'POST',
        headers: {
          'Content-Type': selectedFile.type || 'application/octet-stream',
          'X-File-Name': encodeURIComponent(selectedFile.name),
          'X-File-Description': encodeURIComponent(description.trim()),
        },
        body: selectedFile,
      })
      const data = (await response.json().catch(() => ({}))) as { error?: string }
      if (!response.ok) throw new Error(data.error || 'The upload failed.')
      setSelectedFile(null)
      setDescription('')
      if (fileInput.current) fileInput.current.value = ''
      setNotice(
        'Upload received and placed in quarantine. It will stay private until an administrator approves it.',
      )
      await loadFiles()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The upload failed.')
    } finally {
      setUploading(false)
    }
  }

  const ownFiles = files.filter((file) => file.isOwner)
  const approvedFiles = files.filter((file) => file.status === 'approved')

  return (
    <ContentPage
      wide
      eyebrow="// forum file drop"
      title="Upload and share files"
      description="Send installers, radio files, logs, and archives to NewChirp. Every upload is quarantined for administrator review."
    >
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : !user ? (
        <div className="mx-auto max-w-lg rounded-2xl border border-border bg-card p-8 text-center">
          <LockKeyhole className="mx-auto h-8 w-8 text-primary" />
          <h2 className="mt-4 text-xl font-semibold">Registered users only</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Log in to upload a file or download files that an administrator has approved.
          </p>
          <Button className="mt-5" nativeButton={false} render={<a href="/login" />}>
            Log in
          </Button>
        </div>
      ) : !user.disclaimerAccepted ? (
        <div className="mx-auto max-w-lg rounded-2xl border border-border bg-card p-8 text-center">
          <ShieldCheck className="mx-auto h-8 w-8 text-primary" />
          <h2 className="mt-4 text-xl font-semibold">Accept the forum rules first</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            The file drop uses the same account and community rules as the forum.
          </p>
          <Button className="mt-5" nativeButton={false} render={<a href="/forum" />}>
            Open the forums
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          <section className="overflow-hidden rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 via-card to-accent/20">
            <div className="border-b border-primary/20 px-5 py-4">
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-primary/30 bg-background text-primary">
                  <UploadCloud className="h-5 w-5" />
                </span>
                <div>
                  <h2 className="text-xl font-semibold">Send a file for review</h2>
                  <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
                    Installers, ZIPs, firmware dumps, logs, and radio files are accepted up to{' '}
                    {formatFileSize(maxFileBytes)}. Software and archives are never published
                    automatically.
                  </p>
                </div>
              </div>
            </div>
            <form className="grid gap-5 p-5 lg:grid-cols-[minmax(0,1fr)_minmax(18rem,0.7fr)]" onSubmit={uploadFile}>
              <div>
                <input
                  ref={fileInput}
                  type="file"
                  className="sr-only"
                  onChange={(event) => chooseFile(event.target.files?.[0] || null)}
                />
                <button
                  type="button"
                  onClick={() => fileInput.current?.click()}
                  onDragEnter={(event) => {
                    event.preventDefault()
                    setDragging(true)
                  }}
                  onDragOver={(event) => event.preventDefault()}
                  onDragLeave={() => setDragging(false)}
                  onDrop={(event) => {
                    event.preventDefault()
                    setDragging(false)
                    chooseFile(event.dataTransfer.files?.[0] || null)
                  }}
                  className={`flex min-h-36 w-full flex-col items-center justify-center rounded-xl border border-dashed px-5 py-7 text-center transition-colors ${
                    dragging
                      ? 'border-primary bg-primary/10'
                      : 'border-input bg-background/80 hover:border-primary/50'
                  }`}
                >
                  <FileUp className="h-7 w-7 text-primary" />
                  <span className="mt-2 text-sm font-medium">
                    Drop a file here or choose one
                  </span>
                  <span className="mt-1 text-xs text-muted-foreground">
                    Raw file content stays private while it is reviewed.
                  </span>
                </button>
                {selectedFile && (
                  <div className="mt-3 flex items-center justify-between gap-3 rounded-lg border border-border bg-card px-3 py-2 text-sm">
                    <span className="min-w-0">
                      <span className="block truncate font-medium">{selectedFile.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {formatFileSize(selectedFile.size)}
                      </span>
                    </span>
                    <button
                      type="button"
                      onClick={() => chooseFile(null)}
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground"
                      aria-label="Remove selected file"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
              <div className="flex flex-col">
                <label htmlFor="file-description" className="text-sm font-medium">
                  What should Rick know about this file?
                  <span className="ml-1 font-normal text-muted-foreground">(optional)</span>
                </label>
                <textarea
                  id="file-description"
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  maxLength={500}
                  rows={4}
                  placeholder="Radio model, software version, what you want tested, or where it came from…"
                  className="mt-2 w-full resize-y rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition-colors focus:border-ring focus:ring-2 focus:ring-ring/30"
                />
                <div className="mt-2 flex items-center justify-between gap-3 text-xs text-muted-foreground">
                  <span>{description.length}/500</span>
                  <span>Registered account required</span>
                </div>
                <Button className="mt-auto w-full" type="submit" disabled={uploading || !selectedFile}>
                  {uploading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <UploadCloud className="h-4 w-4" />
                  )}
                  {uploading ? 'Uploading to quarantine…' : 'Upload for review'}
                </Button>
              </div>
            </form>
          </section>

          <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-900 dark:text-amber-200">
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <p>
                Approval is a human review decision, not an antivirus guarantee. Verify a file and
                its SHA-256 before running it. Never disable security software for an upload.
              </p>
            </div>
          </div>

          {error && (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              {error}
            </div>
          )}
          {notice && (
            <div
              role="status"
              className="flex items-start gap-2 rounded-lg border border-primary/30 bg-primary/10 px-4 py-3 text-sm"
            >
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              {notice}
            </div>
          )}

          <section>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-xl font-semibold">Your uploads</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Only you and administrators can see files while they are pending or rejected.
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => void loadFiles()}
                disabled={uploading}
              >
                <RefreshCw className="h-3.5 w-3.5" />
                Refresh
              </Button>
            </div>
            {ownFiles.length === 0 ? (
              <div className="mt-3 rounded-xl border border-dashed border-border bg-card/50 p-8 text-center text-sm text-muted-foreground">
                You have not uploaded a file yet.
              </div>
            ) : (
              <div className="mt-3 flex flex-col gap-3">
                {ownFiles.map((file) => (
                  <FileCard key={file.id} file={file} />
                ))}
              </div>
            )}
          </section>

          <section>
            <div>
              <h2 className="text-xl font-semibold">Approved community files</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Files here have passed manual administrator review. They have not necessarily been
                checked by an antivirus engine.
              </p>
            </div>
            {approvedFiles.length === 0 ? (
              <div className="mt-3 rounded-xl border border-dashed border-border bg-card/50 p-8 text-center text-sm text-muted-foreground">
                No files have been approved yet.
              </div>
            ) : (
              <div className="mt-3 flex flex-col gap-3">
                {approvedFiles.map((file) => (
                  <FileCard key={file.id} file={file} />
                ))}
              </div>
            )}
          </section>
        </div>
      )}
    </ContentPage>
  )
}
